import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { BASE_PRICES, PAIE_ADDON, TAKEOFF_ADDON, MAIN_MODULES, CONSTRUCTION_MODULES, TAKEOFF_CONSTRUCTION, LEVELS, discountForCount, fmt } from "@/data/basePrices";
import { Check, Calculator as CalcIcon, Lock, TrendingDown, Plus } from "lucide-react";

const LEVEL_LABEL = Object.fromEntries(LEVELS.map((l) => [l.key, l.label]));

// Tween numérique fluide pour le total animé
function useTween(value, duration = 550) {
  const [v, setV] = useState(value);
  const ref = useRef(value);
  useEffect(() => {
    const start = ref.current;
    const diff = value - start;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setV(start + diff * e);
      if (p < 1) raf = requestAnimationFrame(step);
      else ref.current = value;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return v;
}

// Prochain palier de rabais à atteindre
function nextTier(count) {
  if (count <= 1) return { need: 2 - count, target: 10 };
  if (count === 2) return { need: 1, target: 15 };
  if (count === 3) return { need: 1, target: 20 };
  return null;
}

export function DiscountCalculator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  // selected[moduleKey] = tier ("starter"|"pro"|"enterprise") si sélectionné, sinon absent
  const [selected, setSelected] = useState({});
  const [paie, setPaie] = useState(false);
  const [takeoff, setTakeoff] = useState(false);
  const [loading, setLoading] = useState(false);

  const comptaTier = selected.compta;
  const devisTier = selected["devis-ai"];
  const takeoffEligible = !!devisTier && !TAKEOFF_CONSTRUCTION;

  useEffect(() => {
    if (!comptaTier && paie) setPaie(false);
    if (!takeoffEligible && takeoff) setTakeoff(false);
  }, [comptaTier, takeoffEligible, paie, takeoff]);

  const toggle = (k) => setSelected((s) => {
    if (CONSTRUCTION_MODULES.includes(k)) return s;
    const next = { ...s };
    if (next[k]) delete next[k];
    else next[k] = "starter";
    return next;
  });

  const setTier = (k, tier) => setSelected((s) => ({ ...s, [k]: tier }));

  const calc = useMemo(() => {
    const chosen = MAIN_MODULES.filter((k) => selected[k]);
    let base = chosen.reduce((sum, k) => sum + BASE_PRICES[k][selected[k]], 0);
    const paieActive = paie && comptaTier;
    if (paieActive) base += PAIE_ADDON[comptaTier];
    const takeoffActive = takeoff && takeoffEligible;
    if (takeoffActive) base += TAKEOFF_ADDON[devisTier];
    const count = chosen.length;
    const discount = discountForCount(count);
    const final = base * (1 - discount / 100);
    return { chosen, base, discount, final, saving: base - final, paieActive, takeoffActive, count };
  }, [selected, paie, comptaTier, takeoff, takeoffEligible, devisTier]);

  const animFinal = useTween(calc.final);
  const nt = nextTier(calc.count);

  const goPay = async () => {
    if (calc.count === 0) return toast.error(t("calc.selectOne"));
    if (!user) {
      toast.info(t("calc.loginFirst"));
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: calc.chosen.map((k) => ({ module: k, plan: selected[k] })),
        payroll: calc.paieActive,
        payroll_plan: comptaTier || null,
        takeoff: calc.takeoffActive,
        takeoff_plan: devisTier || null,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("calc.payError"));
      setLoading(false);
    }
  };

  const TierButtons = ({ current, onPick, testidPrefix }) => (
    <div className="mt-3 grid grid-cols-3 gap-1.5" data-testid={`${testidPrefix}-tiers`}>
      {LEVELS.map((l) => (
        <button
          key={l.key}
          data-testid={`${testidPrefix}-${l.key}`}
          onClick={() => onPick(l.key)}
          className={`rounded-lg py-2 text-xs font-semibold transition-colors ${
            current === l.key ? "bg-[#EAB308] text-black" : "bg-white/5 border border-white/12 text-white/60 hover:bg-white/10"
          }`}
        >
          <span className="block">{l.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="rounded-3xl border border-white/12 bg-[#0a0a0a] p-6 lg:p-8" data-testid="module-calculator">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#EAB308]/40 bg-[#EAB308]/10">
          <CalcIcon className="w-5 h-5 text-[#EAB308]" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">{t("calc.title")}</h3>
          <p className="text-sm text-white/50">{t("calc.subtitle")}</p>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Choix des modules avec palier par module */}
        <div className="space-y-3">
          {MAIN_MODULES.map((k) => {
            const active = !!selected[k];
            const inConstruction = CONSTRUCTION_MODULES.includes(k);
            return (
              <div
                key={k}
                data-testid={`calc-module-${k}`}
                className={`rounded-xl border px-4 py-3 transition-colors ${
                  inConstruction ? "border-white/8 bg-black/20 opacity-60"
                    : active ? "border-[#EAB308]/40 bg-[#EAB308]/[0.05]" : "border-white/10 bg-black/40"
                }`}
              >
                <label className={`flex items-center justify-between gap-3 ${inConstruction ? "cursor-not-allowed" : "cursor-pointer"}`}>
                  <span className="flex items-center gap-2.5 text-sm font-medium text-white/90">
                    <input type="checkbox" checked={active} disabled={inConstruction} onChange={() => toggle(k)} className="h-4 w-4 accent-[#EAB308]" />
                    {inConstruction && <Lock className="w-3.5 h-3.5 text-white/40" />}
                    {BASE_PRICES[k].name}
                  </span>
                  {inConstruction ? (
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-[#f5c77e]">{t("calc.construction")}</span>
                  ) : active && (
                    <span className="text-sm text-[#EAB308] font-mono font-semibold">{fmt(BASE_PRICES[k][selected[k]])}</span>
                  )}
                </label>
                {inConstruction && (
                  <p className="mt-1 text-xs text-white/40">{t("calc.soon")}</p>
                )}
                {active && !inConstruction && (
                  <TierButtons current={selected[k]} onPick={(t) => setTier(k, t)} testidPrefix={`calc-tier-${k}`} />
                )}
              </div>
            );
          })}

          {/* PAIE add-on (suit le palier de COMPTA) */}
          <div
            data-testid="calc-paie"
            className={`rounded-xl border px-4 py-3 ${
              comptaTier ? "border-[#EAB308]/30 bg-[#EAB308]/[0.05]" : "border-white/8 bg-black/20 opacity-60"
            }`}
          >
            <label className={`flex items-center justify-between gap-3 ${comptaTier ? "cursor-pointer" : "cursor-not-allowed"}`}>
              <span className="flex items-center gap-2.5 text-sm font-medium text-white/90">
                <input type="checkbox" checked={paie && !!comptaTier} disabled={!comptaTier} onChange={() => setPaie((v) => !v)} className="h-4 w-4 accent-[#EAB308]" />
                {!comptaTier && <Lock className="w-3.5 h-3.5 text-white/40" />}
                Option {PAIE_ADDON.name}
                {comptaTier && <span className="text-xs text-white/45">({LEVEL_LABEL[comptaTier]})</span>}
              </span>
              {comptaTier && <span className="text-sm text-white/50 font-mono">+{fmt(PAIE_ADDON[comptaTier])}</span>}
            </label>
            {!comptaTier && (
              <p className="mt-1 text-xs text-white/40">{t("calc.paieNote")}</p>
            )}
          </div>

          {/* TAKEOFF add-on (en construction) */}
          <div
            data-testid="calc-takeoff"
            className="rounded-xl border border-white/8 bg-black/20 px-4 py-3 opacity-60"
          >
            <label className="flex items-center justify-between gap-3 cursor-not-allowed">
              <span className="flex items-center gap-2.5 text-sm font-medium text-white/90">
                <input type="checkbox" checked={false} disabled className="h-4 w-4 accent-[#EAB308] cursor-not-allowed" />
                <Lock className="w-3.5 h-3.5 text-white/40" />
                Option {TAKEOFF_ADDON.name}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#f5c77e]" data-testid="calc-takeoff-construction">{t("calc.construction")}</span>
            </label>
            <p className="mt-1 text-xs text-white/40">{t("calc.soon")}</p>
          </div>
        </div>

        {/* Résumé */}
        <div className="rounded-2xl border border-[#EAB308]/25 bg-gradient-to-br from-[#EAB308]/[0.06] to-transparent p-6">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-mono uppercase tracking-wider text-[#EAB308]">{t("calc.selection")}</div>
            <AnimatePresence>
              {calc.discount > 0 && (
                <motion.span
                  key={calc.discount}
                  data-testid="calc-discount-badge"
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.6, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 380, damping: 18 }}
                  className="inline-flex items-center gap-1 rounded-full bg-[#EAB308] px-3 py-1 text-xs font-bold text-black"
                >
                  <TrendingDown className="w-3.5 h-3.5" /> −{calc.discount} %
                </motion.span>
              )}
            </AnimatePresence>
          </div>
          <ul className="mt-3 space-y-1.5 min-h-[60px]">
            {calc.chosen.length === 0 && <li className="text-sm text-white/40">{t("calc.none")}</li>}
            {calc.chosen.map((k) => (
              <li key={k} className="flex items-center justify-between gap-2 text-sm text-white/75">
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#EAB308]" /> {BASE_PRICES[k].name}
                  <span className="text-white/45">· {LEVEL_LABEL[selected[k]]}</span>
                </span>
                <span className="font-mono">{fmt(BASE_PRICES[k][selected[k]])}</span>
              </li>
            ))}
            {calc.paieActive && (
              <li className="flex items-center justify-between gap-2 text-sm text-white/75">
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#EAB308]" /> {PAIE_ADDON.name} {t("calc.optionTag")}
                  <span className="text-white/45">· {LEVEL_LABEL[comptaTier]}</span>
                </span>
                <span className="font-mono">+{fmt(PAIE_ADDON[comptaTier])}</span>
              </li>
            )}
            {calc.takeoffActive && (
              <li className="flex items-center justify-between gap-2 text-sm text-white/75">
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#EAB308]" /> {TAKEOFF_ADDON.name} {t("calc.optionTag")}
                  <span className="text-white/45">· {LEVEL_LABEL[devisTier]}</span>
                </span>
                <span className="font-mono">+{fmt(TAKEOFF_ADDON[devisTier])}</span>
              </li>
            )}
          </ul>

          <div className="mt-4 space-y-2 border-t border-white/10 pt-4 text-sm">
            <div className="flex justify-between text-white/60"><span>{t("calc.regular")}</span><span data-testid="calc-regular" className="font-mono">{fmt(calc.base)}{t("calc.perMonth")}</span></div>
            <div className="flex justify-between text-white/60"><span>{t("calc.multiDiscount")}</span><span data-testid="calc-discount" className="font-mono text-[#EAB308]">{calc.discount} %</span></div>
            <div className="flex justify-between text-white/60"><span>{t("calc.saving")}</span><span data-testid="calc-saving" className="font-mono text-green-400">{fmt(calc.saving)}{t("calc.perMonth")}</span></div>
            <div className="flex justify-between text-white font-bold text-lg pt-1"><span>{t("calc.total")}</span><span data-testid="calc-final" className="font-mono">{fmt(animFinal)}{t("calc.perMonth")}</span></div>
          </div>

          {/* Incitation au palier suivant */}
          <AnimatePresence>
            {nt && (
              <motion.div
                key={`${calc.count}-${nt.target}`}
                data-testid="calc-next-tier"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="mt-3 flex items-center gap-2 rounded-lg border border-[#dc143c]/25 bg-[#dc143c]/[0.06] px-3 py-2 text-xs text-[#f0607a]"
              >
                <Plus className="w-3.5 h-3.5 shrink-0" />
                {calc.count === 0
                  ? t("calc.nudge0")
                  : `${t("calc.nudgeAdd") } ${nt.need} ${nt.need > 1 ? t("calc.modules") : t("calc.module")} ${t("calc.nudgeTo")} −${nt.target} %.`}
              </motion.div>
            )}
          </AnimatePresence>
          <p className="mt-2 text-[11px] text-white/40">{t("calc.disclaimer")}</p>

          <button
            data-testid="calc-pay"
            onClick={goPay}
            disabled={loading}
            className="mt-4 w-full rounded-lg h-11 font-semibold bg-[#EAB308] text-black hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {loading ? t("calc.redirecting") : t("calc.pay")}
          </button>
          <p className="mt-2 text-[11px] text-center text-white/35">{t("calc.payNote")}</p>
        </div>
      </div>
    </div>
  );
}
